import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal } from 'rsuite'
import Select from 'react-select'
import { useAuth0 } from '@auth0/auth0-react'

import SortableTable from '../CommonComponents/SortableTableComponent'
import TableCell from '../CommonComponents/TableCell'
import { toast } from '../CommonComponents/ToastComponent/toast'
import BidAdjustComponent from '../CommonComponents/BidAdjustComponent'
import TableCampaignCell from '../CommonComponents/TableCampaignCell'

import {
  updateKeywordStates,
  updateKeywordBids,
} from '../../redux/actions/bulkEngine'
import { selectCurrentAccount } from '../../redux/reducers/header'

import {
  formatCurrency,
  tableSorter,
  capitalizeFirstLetter,
  calcNewBid,
} from '../../services/helper'

import {
  bulkBidColumnList,
  adjustBidOptions,
} from '../../utils/defaultValues'

import { matchTypes } from '../../utils/filterDef'

const columns = [
  { key: 'campaignName', name: 'Campaign', className: 'col-campaign' },
  { key: 'adgroup_name', name: 'Ad Group', className: 'col-adgroup' },
  { key: 'state', name: 'State' },
  { key: 'matchType', name: 'Match Type' },
  { key: 'bid', name: 'Current Bid' },
  ...bulkBidColumnList,
]

const DupKeywordModal = ({ duplicatedKeyword, onClose }) => {
  const dispatch = useDispatch()
  const { getAccessTokenSilently } = useAuth0()

  const currentAccount = useSelector(selectCurrentAccount)
  const currencySign = useSelector(state => state.header.currencySign)
  const currencyRate = useSelector(state => state.header.currencyRate)
  const campaignTableColumns = useSelector(state => state.pageGlobal.campaignTableColumns)

  const [keywords, setKeywords] = useState([])
  const [selectedKeywords, setSelectedKeywords] = useState([])
  const [isShowAdjustBid, setIsShowAdjustBid] = useState(false)
  const [selectedAdjustBidOption, setSelectedAdjustBidOption] = useState(adjustBidOptions[0])
  const [bidValue, setBidValue] = useState(0)
  const [selectedMatchType, setSelectedMatchType] = useState(matchTypes[0])
  const [isUpdatingStates, setIsUpdatingStates] = useState(false)
  const [isUpdatingBids, setIsUpdatingBids] = useState(false)

  useEffect(() => {
    if (duplicatedKeyword) {
      let records = duplicatedKeyword.children
      if (selectedMatchType.value !== '') {
        records = records.filter(record =>
          (record.match_type || '').toLowerCase() === selectedMatchType.value
        )
      }
      setKeywords(records)
    }
  }, [duplicatedKeyword, selectedMatchType])

  const handlePause = async () => {
    let keywordsChanged = []
    keywords.filter(record => (
      selectedKeywords.indexOf(record.keyword_id) !== -1
      && record.keyword !== '(_targeting_auto_)'
    )).forEach((record) => {
      keywordsChanged.push({
        campaignId: record.campaign_id,
        campaignType: record.campaignType,
        adGroupId: record.adgroup_id,
        keywordId: record.keyword_id,
        // Below information are used for logging in backend.
        adgroupName: record.adgroup_name,
        keyword: record.keyword,
        matchType: capitalizeFirstLetter(record.match_type || ''),
        originalState: record.state,
      })
    })

    // Remove duplicate entries.
    keywordsChanged =  [...new Map(keywordsChanged.map(item => [item.keywordId, item])).values()]

    if (keywordsChanged.length) {
      setIsUpdatingStates(true)
      const accessToken = await getAccessTokenSilently()
      const keywordIdsUpdated = await dispatch(updateKeywordStates(
        accessToken,
        keywordsChanged,
        'paused',
      ))
      setIsUpdatingStates(false)
      setKeywords(duplicatedKeyword.children.map((record) => {
        if (keywordIdsUpdated.includes(record.keyword_id.toString())) {
          return {
            ...record,
            state: 'paused',
          }
        }
        return record
      }))
    }
  }

  const handleAdjustBid = async () => {
    let keywordsChanged = []
    keywords.filter(record => (
      selectedKeywords.indexOf(record.keyword_id) !== -1
      && record.keyword !== '(_targeting_auto_)'
    )).forEach((record) => {
      const newBid = calcNewBid(
        record,
        selectedAdjustBidOption.value,
        bidValue,
        record.campaignType,
        currentAccount?.country_id,
        record.costType
      )

      keywordsChanged.push({
        campaignId: record.campaign_id,
        campaignType: record.campaignType,
        adGroupId: record.adgroup_id,
        keywordId: record.keyword_id,
        bid: parseFloat(newBid.toFixed(2)),
        // Below information are used for logging in backend.
        adgroupName: record.adgroup_name,
        keyword: record.keyword,
        originalBid: record.bid,
        matchType: capitalizeFirstLetter(record.match_type || ''),
      })
    })

    // Remove duplicate entries.
    keywordsChanged =  [...new Map(keywordsChanged.map(item => [item.keywordId, item])).values()]

    if (!keywordsChanged.length) {
      toast.show({
        title: 'Warning',
        description: 'The minimum bid allowed is $0.15. Please check your keywords.',
      })
      return
    }

    setIsUpdatingBids(true)
    const accessToken = await getAccessTokenSilently()
    const keywordIdsUpdated = await dispatch(updateKeywordBids(
      accessToken,
      keywordsChanged,
    ))
    setIsUpdatingBids(false)
    setKeywords(duplicatedKeyword.children.map((record) => {
      if (keywordIdsUpdated.includes(record.keyword_id.toString())) {
        const payload = keywordsChanged.find(p => (
          p.keywordId.toString() === record.keyword_id.toString()
        ))
        if (payload) {
          return {
            ...record,
            bid: payload.bid,
          }
        }
      }
      return record
    }))
  }

  const renderAction = () => {
    if (!selectedKeywords.length) {
      return null
    }

    if (!isShowAdjustBid) {
      const isPauseDisabled = typeof keywords.find(record => (
        selectedKeywords.indexOf(record.keyword_id) !== -1
        && record.state.toLowerCase() !== 'paused'
      )) === 'undefined'

      return (
        <>
          <button
            type="button"
            className="btn btn-red"
            disabled={isUpdatingStates || isPauseDisabled}
            onClick={handlePause}
          >
            Pause
          </button>
          <button
            type="button"
            className="btn btn-light-blue"
            onClick={() => { setIsShowAdjustBid(true) }}
          >
            Adjust Bid
          </button>
        </>
      )
    }

    return (
      <BidAdjustComponent
        adjustBidOption={selectedAdjustBidOption}
        bidValue={bidValue}
        isAdjusting={isUpdatingBids}
        onChangeAdjustBidOption={setSelectedAdjustBidOption}
        onChangeBidValue={setBidValue}
        onApply={handleAdjustBid}
        onCancel={() => { setIsShowAdjustBid(false) }}
      />
    )
  }

  const renderKeyword = record => (
    <>
      <TableCampaignCell
        record={record}
        noLink
      />
      <div className="table-col col-adgroup" title={record.adgroup_name}>
        <span className="contents">
          { record.adgroup_name }
        </span>
      </div>
      <div className="table-col col-state">
        { record.state.toLowerCase() }
      </div>
      <div className="table-col">
        { record.matchType }
      </div>
      <div className="table-col">
        { formatCurrency(record.bid, currencySign, currencyRate) }
      </div>
      {
        bulkBidColumnList.map(column => (
          <TableCell
            key={column.key}
            record={record}
            columnKey={column.key}
            columnSelection={campaignTableColumns}
            currencySign={currencySign}
            currencyRate={currencyRate}
          />
        ))
      }
    </>
  )

  return (
    <Modal className="dup-keyword-modal" backdrop="static" full show={duplicatedKeyword !== null}>
      <Modal.Header onHide={() => { onClose() }}>
        <Modal.Title>
          Duplicated Keyword: { duplicatedKeyword ? duplicatedKeyword.keyword : '' }
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="filter-container">
          <div className="select-wrapper">
            <span>Match Type</span>
            <Select
              classNamePrefix="match-type-selector"
              options={matchTypes}
              value={selectedMatchType}
              onChange={setSelectedMatchType}
            />
          </div>
        </div>
        <SortableTable
          columns={columns}
          defaultSort={['cost', 'desc']}
          sorter={tableSorter(['campaignName', 'adgroup_name', 'state', 'matchType'])}
          className="table-keywords"
          records={keywords}
          idField="keyword_id"
          searchFields={['campaignName', 'adgroup_name']}
          selectedRecords={selectedKeywords}
          hasSticky
          columnEditorId="dupKeywordModal"
          columnList={bulkBidColumnList}
          columnSelection={campaignTableColumns}
          isLoading={isUpdatingStates || isUpdatingBids}
          renderRecord={renderKeyword}
          renderTopRight={renderAction}
          onChange={setSelectedKeywords}
        />
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="rs-btn rs-btn-subtle" onClick={() => onClose()}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default DupKeywordModal
